<script setup lang="ts">
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import dayjs from 'dayjs';
import { type Component, computed, ref, useTemplateRef, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import AddressBookIcon from '@/components/base/assets/AddressBookIcon.vue';
import AutoDeliverySquaredArrowsIcon from '@/components/base/assets/AutoDeliverySquaredArrowsIcon.vue';
import HeartIcon from '@/components/base/assets/HeartIcon.vue';
import OrderHistoryIcon from '@/components/base/assets/OrderHistoryIcon.vue';
import ReorderIcon from '@/components/base/assets/ReorderIcon.vue';
import RoundedPersonIcon from '@/components/base/assets/RoundedPersonIcon.vue';
import SignOutIcon from '@/components/base/assets/SignOutIcon.vue';
import WalletIcon from '@/components/base/assets/WalletIcon.vue';
import PopperWrapper from '@/components/base/layout/PopperWrapper.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';
import { useRouterLinks } from '@/composables/navigation/useRouterLinks';
import { useCheckout } from '@/composables/useCheckout';
import { pluralizeBy } from '@/filters/pluralize';
import { useOverlay } from '@/stores/overlay';

const props = defineProps<{
  userCreatedAt?: string;
  userFirstName?: string;
}>();

const emit = defineEmits<{ goToAccount: [] }>();

interface MenuLink {
  destination: string;
  icon: Component;
  text: string;
}

const links: MenuLink[] = [
  {
    destination: '/account/easy-reorder',
    icon: ReorderIcon,
    text: 'Easy Reorder',
  },
  {
    destination: '/account/order-history',
    icon: OrderHistoryIcon,
    text: 'Order History',
  },
  {
    destination: '/account/auto-delivery',
    icon: AutoDeliverySquaredArrowsIcon,
    text: 'Subscriptions',
  },
  {
    destination: '/account/wallet',
    icon: WalletIcon,
    text: 'Wallet',
  },
  {
    destination: '/account/address-book',
    icon: AddressBookIcon,
    text: 'Address Book',
  },
  {
    destination: '/account/favorites',
    icon: HeartIcon,
    text: 'Favorites',
  },
];

const store = useStore();
const { cxMode } = useCheckout(store);

const isSignedIn = computed(() => !!props.userCreatedAt || cxMode.value);
const createdAtDate = computed(() => dayjs(props.userCreatedAt));

const timeWithUs = computed<string>(() => {
  const currentDate = dayjs().startOf('day');
  const dateDiffInMonths = currentDate.diff(createdAtDate.value, 'months');
  const yearsWithUs = Math.floor(dateDiffInMonths / 12);
  const leftOverMonths = dateDiffInMonths % 12;

  if (yearsWithUs === 0) {
    if (leftOverMonths === 0) return '';
    return `${leftOverMonths} ${pluralizeBy('month', leftOverMonths)}`;
  }

  return `${yearsWithUs} ${pluralizeBy('year', yearsWithUs)}${
    leftOverMonths > 0 ? ` ${leftOverMonths} ${pluralizeBy('month', leftOverMonths)}` : ''
  }`;
});

const personalizedMessage = computed<string>(() => {
  if (timeWithUs.value === '') return 'Welcome back! Great to see you again!';
  return `You've been with us for ${timeWithUs.value}`;
});

const personalizedGreeting = computed<string>(
  () => `Hello, ${props.userFirstName ? props.userFirstName : 'Welcome'}!`,
);

const linksContainer = useTemplateRef('linksContainer');
useRouterLinks(linksContainer, useRouter());

const overlay = useOverlay();
const popperRef = useTemplateRef<HTMLElement>('popperRef');
const { activate, deactivate } = useFocusTrap(popperRef);

const show = ref(false);
const handleTrigger = (event: KeyboardEvent | MouseEvent) => {
  if ('key' in event && event.key === ' ' && isSignedIn.value) {
    show.value = true;
    return;
  }

  emit('goToAccount');
};
watch(show, (value) => {
  if (value) {
    overlay.show(true);
    activate();
  } else {
    overlay.hide();
    deactivate();
  }
});
</script>

<template>
  <PopperWrapper
    class="legacy-max-width"
    ref="popperRef"
    :show
    @mouseenter="isSignedIn && (show = true)"
    @mouseleave="isSignedIn && (show = false)"
  >
    <UnstyledButton
      class="flex relative gap-2 items-center px-2 py-1 font-semibold rounded-full max-w-40 hover:bg-nuts-stone-100"
      @click="handleTrigger"
      @keyup.escape="show = false"
      @keyup.space.capture.prevent="handleTrigger"
    >
      <RoundedPersonIcon class="text-zinc-800" :size="32" />
      <SmallBodyText class="truncate whitespace-nowrap">
        {{ isSignedIn ? `Hi, ${userFirstName ?? 'Welcome'}` : 'Sign In' }}
      </SmallBodyText>
    </UnstyledButton>
    <template v-if="isSignedIn" #content>
      <div class="flex flex-col py-2 w-64" ref="linksContainer">
        <div class="flex flex-col gap-2 p-4 mb-1">
          <BaseBodyText class="font-bold"> {{ personalizedGreeting }} </BaseBodyText>
          <BaseBodyText class="font-normal">
            {{ personalizedMessage }}
          </BaseBodyText>
        </div>
        <ul class="flex flex-col py-2 font-normal list-border">
          <li v-for="link in links" :key="link.text">
            <a
              class="inline-flex gap-4 items-center px-4 py-2 w-full text-sm hover:no-underline hover:bg-nuts-stone-100 focus:bg-nuts-stone-100"
              :href="link.destination"
            >
              <component :is="link.icon" class="text-zinc-800" :size="24" />
              <BaseBodyText class="text-black">{{ link.text }}</BaseBodyText>
            </a>
          </li>
        </ul>
        <a
          class="inline-flex gap-4 items-center px-4 py-2 mt-2 w-full text-sm hover:no-underline hover:bg-nuts-stone-100 focus:bg-nuts-stone-100"
          href="/sign/out"
        >
          <SignOutIcon class="text-zinc-800" :size="24" />
          <BaseBodyText class="font-normal text-black">Sign Out</BaseBodyText>
        </a>
      </div>
    </template>
  </PopperWrapper>
</template>

<style scoped>
.list-border {
  @apply border-t border-b border-neutral-300 border-solid;
}
</style>
